import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, HostListener, Input, OnDestroy, OnInit, effect } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AppStateService } from 'src/app/shared/services/helpers/app-state.service';
import { ToastService } from 'src/app/shared/services/toast/toast.service';
import { LtmsLoaderComponent } from 'src/app/shared/components/ltms-loader/ltms-loader.component';
import { FileUploadService } from 'src/app/shared/services/file-upload/file-upload.service';
import { ChannelSetupService, firstValidationMessage } from 'src/app/shared/services/channel-setup/channel-setup.service';
import {
  ChannelBlacklistEntry,
  ChannelBlacklistFieldOption,
  ChannelBlacklistSetup,
  emptyBlacklistSetup,
} from 'src/app/shared/services/channel-setup/channel-setup.models';

const TOAST_MS = 15000;
const IMPORT_ROW_LIMIT = 5000;
const EMAIL_PATTERN = /^[A-Za-z0-9._%+-]+@[A-Za-z0-9.-]+\.[A-Za-z]{2,}$/;
const VALUE_MAX_LENGTH = 256;
const PHONE_MIN_DIGITS = 10;
const PHONE_MAX_DIGITS = 15;
const MAX_BLACKLIST_FIELDS = 2;
const ACCOUNT_ENABLED_WARNING = 'Account-level Blacklist / DND is enabled. You can still use it for this project. When this project service is on, sends use this project\'s fields and list first.';
const HIDDEN_TAG_FIELDS = new Set([
  'tag_selected_language_key',
  'tag_feedback_reference_token',
]);
const DEFAULT_PRIVATE_FIELDS: Array<{ id: string; label: string }> = [
  { id: 'tag_private_phone_number', label: 'Phone Number' },
  { id: 'tag_private_email_address', label: 'Email Address' },
  { id: 'tag_private_whatsapp_number', label: 'WhatsApp Number' },
  { id: 'tag_private_user_name', label: 'Name' },
];

function isSelectableTagField(id: string): boolean {
  const fieldId = String(id || '');
  return fieldId.startsWith('tag_') && !HIDDEN_TAG_FIELDS.has(fieldId);
}

@Component({
  selector: 'app-channel-blacklist',
  templateUrl: './channel-blacklist.component.html',
  styleUrls: ['./channel-blacklist.component.scss'],
  standalone: true,
  imports: [CommonModule, FormsModule, LtmsLoaderComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChannelBlacklistComponent implements OnInit, OnDestroy {
  @Input() isWriteAllowed = false;

  brandId: string | null = null;
  loading = false;
  saving = false;
  adding = false;
  importing = false;
  loadError = '';
  setup: ChannelBlacklistSetup = emptyBlacklistSetup();
  enabled = false;
  selectedFields: string[] = [];
  draftKey = '';
  draftValue = '';
  fieldDraft = '';
  fieldQuery = '';
  fieldMenuOpen = false;
  draftFieldMenuOpen = false;
  listFieldKey = '';
  selectedSearchField = '';
  listSearchQuery = '';
  showSearchFields = false;
  private appliedListSearch = '';
  private appliedSearchField = '';
  entries: ChannelBlacklistEntry[] = [];
  page = 1;
  pageSize = 10;
  total = 0;
  entriesLoading = false;
  csvPreview: Array<{ key: string; value: string }> = [];
  csvColumns: string[] = [];
  csvGrid: string[][] = [];
  csvCellCount = 0;
  csvInvalid: string[] = [];
  csvName = '';
  confirmRemove: ChannelBlacklistEntry | null = null;
  confirmImport = false;
  setupOpen = false;
  setupStep: 'service' | 'records' = 'service';
  readonly accountEnabledWarning = ACCOUNT_ENABLED_WARNING;

  constructor(
    private readonly appStateService: AppStateService,
    private readonly channelSetupService: ChannelSetupService,
    private readonly fileUploadService: FileUploadService,
    private readonly toastService: ToastService,
    private readonly changeDetector: ChangeDetectorRef,
    private readonly host: ElementRef<HTMLElement>,
  ) {
    effect(() => {
      if (!this.appStateService.initialized()) {
        return;
      }
      const authDetails: any = this.appStateService.managerAccess();
      const loggedIn = authDetails?.loggedInManager ?? {};
      const brandDetails: any = this.appStateService.brandDetails();
      const nextBrandId = loggedIn.brand_id || brandDetails?._id || null;
      if (nextBrandId && nextBrandId !== this.brandId) {
        this.brandId = nextBrandId;
        this.load();
      }
    });
  }

  ngOnInit(): void {
    this.host.nativeElement.addEventListener('click', this.onUnauthorizedClick, true);
  }

  ngOnDestroy(): void {
    this.host.nativeElement.removeEventListener('click', this.onUnauthorizedClick, true);
  }

  private readonly onUnauthorizedClick = (event: Event): void => {
    if (this.isWriteAllowed) {
      return;
    }
    const target = event.target as HTMLElement | null;
    if (!target?.closest?.('.is-unauthorized')) {
      return;
    }
    event.preventDefault();
    event.stopPropagation();
    this.toastService.showPopup('ERROR', 'Not authorized', 'Not authorized', TOAST_MS);
  };

  get accountEnabled(): boolean {
    return Boolean(this.setup.account_enabled ?? this.setup.account?.is_blacklist_enabled);
  }

  get accountEnabledMessage(): string {
    return this.setup.account_enabled_warning || this.accountEnabledWarning;
  }

  get canEdit(): boolean {
    return this.isWriteAllowed;
  }

  get configDirty(): boolean {
    const savedFields = [...this.savedFields].sort().join('|');
    const nextFields = [...this.selectedFields].sort().join('|');
    return this.enabled !== Boolean(this.setup.is_blacklist_enabled) || savedFields !== nextFields;
  }

  get fieldOptions(): ChannelBlacklistFieldOption[] {
    const options = [...DEFAULT_PRIVATE_FIELDS];
    (this.setup.field_options || []).forEach((item) => {
      if (!isSelectableTagField(item.id) || options.some((option) => option.id === item.id)) {
        return;
      }
      options.push(item);
    });
    this.selectedFields.forEach((id) => {
      if (!isSelectableTagField(id)) {
        return;
      }
      if (!options.some((item) => item.id === id)) {
        options.push({ id, label: id });
      }
    });
    return options;
  }

  get filteredFieldOptions(): ChannelBlacklistFieldOption[] {
    const query = String(this.fieldQuery || '').trim().toLowerCase();
    if (!query) {
      return this.fieldOptions;
    }
    return this.fieldOptions.filter((item) => {
      return item.label.toLowerCase().includes(query) || item.id.toLowerCase().includes(query);
    });
  }

  get fieldMenuLabel(): string {
    if (!this.selectedFields.length) {
      return 'Select tag fields';
    }
    if (this.selectedFields.length === 1) {
      return this.fieldLabel(this.selectedFields[0]);
    }
    return `${this.selectedFields.length} fields selected`;
  }

  get searchFieldOptions(): ChannelBlacklistFieldOption[] {
    return this.savedFields.map((id) => ({
      id,
      label: this.fieldLabel(id),
    }));
  }

  get listFieldLabel(): string {
    const id = this.selectedSearchField || this.listFieldKey || this.savedFields[0] || '';
    return id ? this.fieldLabel(id) : 'Select field';
  }

  get searchPlaceholder(): string {
    return `Search by ${this.listFieldLabel}`;
  }

  get isTokenizationProject(): boolean {
    const brand: any = this.appStateService.brandDetails();
    const ts = brand?.security_settings?.tokenization_server;
    return Boolean(ts && typeof ts === 'object' && !Array.isArray(ts) && Object.keys(ts).length);
  }

  get valueMaxLength(): number {
    return VALUE_MAX_LENGTH;
  }

  get draftFieldLabel(): string {
    return this.draftKey ? this.fieldLabel(this.draftKey) : 'Select field';
  }

  get draftValuePlaceholder(): string {
    if (this.isTokenizationProject) {
      return 'Enter value';
    }
    if (this.isEmailField(this.draftKey)) {
      return 'name@example.com';
    }
    if (this.isPhoneField(this.draftKey)) {
      return '9876543210';
    }
    if (this.isWhatsappField(this.draftKey)) {
      return '9876543210';
    }
    return 'Enter value';
  }

  get availableFieldOptions(): ChannelBlacklistFieldOption[] {
    return this.fieldOptions.filter((item) => !this.selectedFields.includes(item.id));
  }

  get rangeStart(): number {
    if (!this.total) {
      return 0;
    }
    return ((this.page - 1) * this.pageSize) + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.page * this.pageSize, this.total);
  }

  get pageCount(): number {
    return Math.max(1, Math.ceil(this.total / this.pageSize));
  }

  get savedEnabled(): boolean {
    return Boolean(this.setup.is_blacklist_enabled);
  }

  get savedFields(): string[] {
    return (this.setup.blacklist_fields || []).filter((id) => isSelectableTagField(id));
  }

  get canQuickAdd(): boolean {
    return this.savedEnabled && this.savedFields.length > 0 && this.canEdit;
  }

  get canGoToRecords(): boolean {
    return this.selectedFields.length > 0;
  }

  get atFieldLimit(): boolean {
    return this.selectedFields.length >= MAX_BLACKLIST_FIELDS;
  }

  get searchNeedsEnter(): boolean {
    const next = String(this.listSearchQuery || '').trim();
    return Boolean(next) && (next !== this.appliedListSearch || (this.selectedSearchField || this.listFieldKey) !== this.appliedSearchField);
  }

  get nextSetupDisabledReason(): string {
    if (!this.canGoToRecords) {
      return 'Choose at least one field before you add values.';
    }
    return '';
  }

  get busy(): boolean {
    return this.saving || this.adding || this.importing;
  }

  load(): void {
    if (!this.brandId) {
      return;
    }
    this.loading = true;
    this.loadError = '';
    this.channelSetupService.getBlacklist(this.brandId).subscribe({
      next: (data) => {
        this.applySetup(data);
        this.loading = false;
        this.loadEntries({ preferFieldWithRows: true });
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.loading = false;
        this.loadError = firstValidationMessage(error) || 'Could not load Blacklist / DND.';
        this.changeDetector.markForCheck();
      },
    });
  }

  loadEntries(options: { preferFieldWithRows?: boolean } = {}): void {
    if (!this.brandId) {
      return;
    }
    if (!this.listFieldKey) {
      this.listFieldKey = this.savedFields[0] || '';
    }
    if (!this.selectedSearchField) {
      this.selectedSearchField = this.listFieldKey;
    }
    if (!this.listFieldKey) {
      this.entries = [];
      this.total = 0;
      this.entriesLoading = false;
      this.changeDetector.markForCheck();
      return;
    }
    this.entriesLoading = true;
    const search = String(this.appliedListSearch || '').trim();
    const requestedKey = this.listFieldKey;
    this.channelSetupService.listBlacklistEntries(this.brandId, {
      key: requestedKey,
      ...(search ? { q: search } : {}),
      page: this.page,
      page_size: this.pageSize,
    }).subscribe({
      next: (data) => {
        const entries = Array.isArray(data?.entries) ? data.entries : [];
        const total = Number(data?.total) || 0;
        if (options.preferFieldWithRows && !search && total === 0) {
          const currentIndex = this.savedFields.indexOf(requestedKey);
          const nextKey = this.savedFields.find((_id, index) => index > currentIndex);
          if (nextKey) {
            this.listFieldKey = nextKey;
            this.selectedSearchField = nextKey;
            this.page = 1;
            this.loadEntries({ preferFieldWithRows: true });
            return;
          }
        }
        this.entries = entries;
        this.total = total;
        this.page = Number(data?.page) || 1;
        this.entriesLoading = false;
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.entriesLoading = false;
        this.toastService.showPopup('ERROR', 'Blacklist / DND', firstValidationMessage(error) || 'Could not load values.', TOAST_MS);
        this.changeDetector.markForCheck();
      },
    });
  }

  addSelectedField(): void {
    if (!this.canEdit) {
      return;
    }
    const id = String(this.fieldDraft || '').trim();
    if (!id || !isSelectableTagField(id) || this.selectedFields.includes(id)) {
      return;
    }
    if (this.selectedFields.length >= MAX_BLACKLIST_FIELDS) {
      this.toastService.showPopup('ERROR', 'Blacklist / DND', `Choose at most ${MAX_BLACKLIST_FIELDS} fields.`, TOAST_MS);
      return;
    }
    this.selectedFields = [...this.selectedFields, id];
    if (!this.draftKey) {
      this.draftKey = id;
    }
    this.fieldDraft = this.availableFieldOptions[0]?.id || '';
  }

  toggleField(id: string): void {
    if (!this.canEdit || !isSelectableTagField(id)) {
      return;
    }
    if (this.selectedFields.includes(id)) {
      this.removeSelectedField(id);
      return;
    }
    if (this.selectedFields.length >= MAX_BLACKLIST_FIELDS) {
      this.toastService.showPopup('ERROR', 'Blacklist / DND', `Choose at most ${MAX_BLACKLIST_FIELDS} fields.`, TOAST_MS);
      return;
    }
    this.selectedFields = [...this.selectedFields, id];
    if (!this.draftKey) {
      this.draftKey = id;
    }
    this.changeDetector.markForCheck();
  }

  toggleFieldMenu(event: Event): void {
    event.stopPropagation();
    if (!this.canEdit) {
      return;
    }
    this.fieldMenuOpen = !this.fieldMenuOpen;
    this.showSearchFields = false;
    this.draftFieldMenuOpen = false;
    if (this.fieldMenuOpen) {
      this.fieldQuery = '';
    }
    this.changeDetector.markForCheck();
  }

  onFieldQueryChange(): void {
    this.changeDetector.markForCheck();
  }

  @HostListener('document:click', ['$event'])
  closeFieldMenu(event?: Event): void {
    const target = event?.target as HTMLElement | null;
    if (target?.closest?.('.unsub-search') || target?.closest?.('.bl-add-one__field')) {
      return;
    }
    if (!this.fieldMenuOpen && !this.showSearchFields && !this.draftFieldMenuOpen) {
      return;
    }
    this.fieldMenuOpen = false;
    this.showSearchFields = false;
    this.draftFieldMenuOpen = false;
    this.changeDetector.markForCheck();
  }

  toggleSearchFields(event: Event): void {
    event.stopPropagation();
    this.showSearchFields = !this.showSearchFields;
    this.fieldMenuOpen = false;
    this.draftFieldMenuOpen = false;
    this.changeDetector.markForCheck();
  }

  chooseSearchField(id: string): void {
    if (!id || !this.savedFields.includes(id)) {
      return;
    }
    this.selectedSearchField = id;
    this.showSearchFields = false;
    const next = String(this.listSearchQuery || '').trim();
    if (!next) {
      this.listFieldKey = id;
      this.appliedListSearch = '';
      this.appliedSearchField = id;
      this.page = 1;
      this.loadEntries();
      return;
    }
    this.changeDetector.markForCheck();
  }

  toggleDraftFieldMenu(event: Event): void {
    event.stopPropagation();
    if (!this.canEdit) {
      return;
    }
    this.draftFieldMenuOpen = !this.draftFieldMenuOpen;
    this.fieldMenuOpen = false;
    this.showSearchFields = false;
    this.changeDetector.markForCheck();
  }

  chooseDraftField(id: string, event?: Event): void {
    event?.stopPropagation();
    if (!id || !this.selectedFields.includes(id)) {
      return;
    }
    this.draftKey = id;
    this.draftFieldMenuOpen = false;
    this.changeDetector.markForCheck();
  }

  onListSearchQueryChange(): void {
    const next = String(this.listSearchQuery || '').trim();
    if (!next && this.appliedListSearch) {
      this.clearListSearch();
      return;
    }
    this.changeDetector.markForCheck();
  }

  applyListSearch(): void {
    const next = String(this.listSearchQuery || '').trim();
    if (!next) {
      return;
    }
    this.appliedListSearch = next;
    this.appliedSearchField = this.selectedSearchField || this.listFieldKey;
    this.listFieldKey = this.appliedSearchField;
    this.page = 1;
    this.loadEntries();
  }

  clearListSearch(): void {
    this.listSearchQuery = '';
    this.appliedListSearch = '';
    if (this.selectedSearchField) {
      this.listFieldKey = this.selectedSearchField;
      this.appliedSearchField = this.selectedSearchField;
    }
    this.page = 1;
    this.loadEntries();
  }

  removeSelectedField(id: string): void {
    if (!this.canEdit) {
      return;
    }
    this.selectedFields = this.selectedFields.filter((item) => item !== id);
    if (this.draftKey === id) {
      this.draftKey = this.selectedFields[0] || '';
    }
    if (!this.fieldDraft) {
      this.fieldDraft = this.availableFieldOptions[0]?.id || '';
    }
    this.changeDetector.markForCheck();
  }

  openSetup(): void {
    this.setupStep = 'service';
    this.setupOpen = true;
    this.changeDetector.markForCheck();
  }

  openRecordsSetup(): void {
    if (!this.canQuickAdd) {
      this.openSetup();
      return;
    }
    this.setupStep = 'records';
    this.setupOpen = true;
    this.changeDetector.markForCheck();
  }

  closeSetup(): void {
    this.setupOpen = false;
    this.setupStep = 'service';
    this.changeDetector.markForCheck();
  }

  setSetupStep(step: 'service' | 'records'): void {
    if (step === 'records' && !this.canGoToRecords) {
      this.toastService.showPopup('ERROR', 'Blacklist / DND', this.nextSetupDisabledReason, TOAST_MS);
      return;
    }
    this.setupStep = step;
    this.changeDetector.markForCheck();
  }

  goToRecords(): void {
    if (!this.canGoToRecords) {
      this.toastService.showPopup('ERROR', 'Blacklist / DND', this.nextSetupDisabledReason, TOAST_MS);
      return;
    }
    if (this.configDirty) {
      this.saveConfig(false, () => {
        this.setupStep = 'records';
      });
      return;
    }
    this.setupStep = 'records';
    this.changeDetector.markForCheck();
  }

  discardConfig(): void {
    this.enabled = Boolean(this.setup.is_blacklist_enabled);
    this.selectedFields = [...(this.setup.blacklist_fields || [])].filter((id) => isSelectableTagField(id));
    this.draftKey = this.selectedFields[0] || '';
    this.fieldDraft = this.availableFieldOptions[0]?.id || '';
  }

  saveConfig(closePanel = true, afterSave?: () => void): void {
    if (!this.brandId || !this.canEdit || this.saving) {
      return;
    }
    if (!this.configDirty) {
      afterSave?.();
      if (closePanel) {
        this.closeSetup();
      }
      return;
    }
    if (this.enabled && !this.selectedFields.length) {
      this.toastService.showPopup('ERROR', 'Blacklist / DND', 'Choose at least one field when Blacklist / DND is on.', TOAST_MS);
      return;
    }
    this.saving = true;
    this.channelSetupService.saveBlacklist(this.brandId, {
      is_blacklist_enabled: this.enabled,
      blacklist_fields: this.selectedFields,
    }).subscribe({
      next: (data) => {
        this.saving = false;
        this.applySetup(data);
        this.page = 1;
        this.loadEntries({ preferFieldWithRows: true });
        this.toastService.showPopup('SUCCESS', 'Blacklist / DND', 'Settings saved.', TOAST_MS);
        afterSave?.();
        if (closePanel) {
          this.closeSetup();
        }
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.saving = false;
        this.toastService.showPopup('ERROR', 'Blacklist / DND', firstValidationMessage(error) || 'Could not save settings.', TOAST_MS);
        this.changeDetector.markForCheck();
      },
    });
  }

  clearDraftValue(event?: Event): void {
    event?.preventDefault();
    event?.stopPropagation();
    this.draftValue = '';
    this.changeDetector.markForCheck();
  }

  addEntry(): void {
    if (!this.brandId || !this.canEdit || this.adding) {
      return;
    }
    const key = String(this.draftKey || '').trim();
    const value = String(this.draftValue || '').trim();
    if (!key || !value) {
      this.toastService.showPopup('ERROR', 'Blacklist / DND', 'Choose a field and enter a value.', TOAST_MS);
      return;
    }
    const invalid = this.blacklistValueError(key, value);
    if (invalid) {
      this.toastService.showPopup('ERROR', 'Blacklist / DND', invalid, TOAST_MS);
      return;
    }
    this.adding = true;
    this.channelSetupService.addBlacklistEntry(this.brandId, { key, value }).subscribe({
      next: () => {
        this.adding = false;
        this.draftValue = '';
        this.listFieldKey = key;
        this.listSearchQuery = '';
        this.appliedListSearch = '';
        this.page = 1;
        this.toastService.showPopup('SUCCESS', 'Blacklist / DND', 'Value added.', TOAST_MS);
        this.loadEntries();
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.adding = false;
        this.toastService.showPopup('ERROR', 'Blacklist / DND', firstValidationMessage(error) || 'Could not add value.', TOAST_MS);
        this.changeDetector.markForCheck();
      },
    });
  }

  async onCsvFile(event: Event): Promise<void> {
    const input = event.target as HTMLInputElement;
    const file = input.files?.[0];
    input.value = '';
    if (!file || !this.canEdit) {
      return;
    }
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.toastService.showPopup('ERROR', 'Blacklist / DND', 'Upload a CSV file.', TOAST_MS);
      return;
    }
    const parsed = await this.fileUploadService.parseCSV(file);
    if (parsed.error || !parsed.data?.length) {
      this.toastService.showPopup('ERROR', 'Blacklist / DND', parsed.error || 'The file is empty.', TOAST_MS);
      return;
    }
    const matrix = this.csvDataToMatrix(parsed.data);
    const result = this.parseCsvMatrix(matrix, this.draftKey, this.selectedFields);
    if (result.rows.length > IMPORT_ROW_LIMIT) {
      this.toastService.showPopup('ERROR', 'Blacklist / DND', `Upload at most ${IMPORT_ROW_LIMIT} rows.`, TOAST_MS);
      return;
    }
    this.csvName = file.name;
    this.csvGrid = result.grid;
    this.csvColumns = result.columns;
    this.csvCellCount = result.rows.length;
    this.csvPreview = this.uniqueImportRows(result.rows);
    this.csvInvalid = result.invalid;
    if (!result.rows.length) {
      this.toastService.showPopup('ERROR', 'Blacklist / DND', result.invalid[0] || 'No valid rows in this file.', TOAST_MS);
    }
    this.changeDetector.markForCheck();
  }

  clearCsv(): void {
    this.csvPreview = [];
    this.csvColumns = [];
    this.csvGrid = [];
    this.csvCellCount = 0;
    this.csvInvalid = [];
    this.csvName = '';
    this.confirmImport = false;
  }

  downloadSampleCsv(): void {
    const fields = this.selectedFields.length
      ? this.selectedFields
      : (this.draftKey ? [this.draftKey] : ['tag_product']);
    const lines = [fields.map((id) => this.csvCell(id)).join(',')];
    lines.push(fields.map((id, index) => this.csvCell(this.sampleValueForField(id, index))).join(','));
    const blob = new Blob([`${lines.join('\r\n')}\r\n`], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'blacklist-dnd-sample.csv';
    link.click();
    URL.revokeObjectURL(url);
  }

  private sampleValueForField(id: string, index: number): string {
    const fieldId = String(id || '').toLowerCase();
    if (fieldId.includes('email')) {
      return 'name@example.com';
    }
    if (fieldId.includes('phone') || fieldId.includes('whatsapp')) {
      return '9876543210';
    }
    return `example-value-${index + 1}`;
  }

  private csvCell(value: string): string {
    const text = String(value ?? '');
    if (/[",\r\n]/.test(text)) {
      return `"${text.replace(/"/g, '""')}"`;
    }
    return text;
  }

  startImport(): void {
    if (!this.csvPreview.length || !this.canEdit) {
      return;
    }
    this.confirmImport = true;
  }

  confirmImportNow(): void {
    if (!this.brandId || !this.csvPreview.length) {
      this.confirmImport = false;
      return;
    }
    this.confirmImport = false;
    this.importing = true;
    this.channelSetupService.importBlacklistEntries(this.brandId, this.csvPreview).subscribe({
      next: (data) => {
        this.importing = false;
        const importedKey = this.csvPreview[0]?.key;
        this.clearCsv();
        if (importedKey && this.savedFields.includes(importedKey)) {
          this.listFieldKey = importedKey;
        }
        this.listSearchQuery = '';
        this.appliedListSearch = '';
        this.page = 1;
        const skipped = data.skipped_duplicate ? `, ${data.skipped_duplicate} already listed` : '';
        this.toastService.showPopup('SUCCESS', 'Blacklist / DND', `${data.added} added${skipped}.`, TOAST_MS);
        this.loadEntries();
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.importing = false;
        this.toastService.showPopup('ERROR', 'Blacklist / DND', firstValidationMessage(error) || 'Could not import values.', TOAST_MS);
        this.changeDetector.markForCheck();
      },
    });
  }

  askRemove(row: ChannelBlacklistEntry): void {
    if (!this.canEdit) {
      return;
    }
    this.confirmRemove = row;
  }

  confirmRemoveNow(): void {
    const row = this.confirmRemove;
    this.confirmRemove = null;
    if (!this.brandId || !row?.id) {
      return;
    }
    this.channelSetupService.deleteBlacklistEntry(this.brandId, row.id).subscribe({
      next: () => {
        this.toastService.showPopup('SUCCESS', 'Blacklist / DND', 'Value removed.', TOAST_MS);
        this.loadEntries();
        this.changeDetector.markForCheck();
      },
      error: (error) => {
        this.toastService.showPopup('ERROR', 'Blacklist / DND', firstValidationMessage(error) || 'Could not remove value.', TOAST_MS);
        this.changeDetector.markForCheck();
      },
    });
  }

  goToFirstPage(): void {
    if (this.page <= 1) {
      return;
    }
    this.page = 1;
    this.loadEntries();
  }

  goToLastPage(): void {
    if (this.page >= this.pageCount) {
      return;
    }
    this.page = this.pageCount;
    this.loadEntries();
  }

  nextPage(): void {
    if (this.page >= this.pageCount) {
      return;
    }
    this.page += 1;
    this.loadEntries();
  }

  previousPage(): void {
    if (this.page <= 1) {
      return;
    }
    this.page -= 1;
    this.loadEntries();
  }

  fieldLabel(id: string): string {
    return this.fieldOptions.find((item) => item.id === id)?.label || id;
  }

  createdOn(row: ChannelBlacklistEntry): string | Date | null {
    return row?.created_date || null;
  }

  createdBy(row: ChannelBlacklistEntry): string {
    const actor = row?.created_by;
    if (!actor) {
      return '—';
    }
    return actor.name || actor.email_address || '—';
  }

  createdByTitle(row: ChannelBlacklistEntry): string {
    const actor = row?.created_by;
    if (!actor) {
      return '';
    }
    return [actor.name, actor.email_address].filter(Boolean).join(' · ');
  }

  private applySetup(data: ChannelBlacklistSetup): void {
    this.setup = { ...emptyBlacklistSetup(), ...data };
    this.enabled = Boolean(this.setup.is_blacklist_enabled);
    this.selectedFields = [...(this.setup.blacklist_fields || [])].filter((id) => isSelectableTagField(id));
    this.listFieldKey = this.savedFields.includes(this.listFieldKey)
      ? this.listFieldKey
      : (this.savedFields[0] || '');
    this.draftKey = this.selectedFields[0] || '';
    this.fieldDraft = this.availableFieldOptions[0]?.id || '';
  }

  private uniqueImportRows(rows: Array<{ key: string; value: string }>): Array<{ key: string; value: string }> {
    const seen = new Set<string>();
    const unique: Array<{ key: string; value: string }> = [];
    rows.forEach((row) => {
      const key = String(row.key || '').trim();
      const value = this.normalizeImportValue(key, row.value);
      if (!key || !value || this.blacklistValueError(key, row.value)) {
        return;
      }
      const token = `${key}|${value}`;
      if (seen.has(token)) {
        return;
      }
      seen.add(token);
      unique.push({ key, value });
    });
    return unique;
  }

  private isEmailField(key: string): boolean {
    return String(key || '').toLowerCase().includes('email');
  }

  private isWhatsappField(key: string): boolean {
    return String(key || '').toLowerCase().includes('whatsapp');
  }

  private isPhoneField(key: string): boolean {
    const field = String(key || '').toLowerCase();
    if (this.isEmailField(field) || this.isWhatsappField(field)) {
      return false;
    }
    return field === 'tag_private_phone_number' || field.includes('phone');
  }

  private normalizePhoneValue(value: string): string {
    let next = String(value || '').trim().replace(/[\s-]/g, '');
    if (next.startsWith('+91') && next.length > 3) {
      next = next.slice(3);
    } else if (next.startsWith('91') && next.length > 10) {
      next = next.slice(2);
    }
    if (next.startsWith('0') && next.length > 10) {
      next = next.slice(1);
    }
    return next;
  }

  private blacklistValueError(key: string, value: string): string {
    const field = String(key || '').trim();
    const next = String(value || '').trim();
    if (!field) {
      return 'Choose a field.';
    }
    if (!next) {
      return 'Enter a value.';
    }
    if (next.length > VALUE_MAX_LENGTH) {
      return `Value can be at most ${VALUE_MAX_LENGTH} characters.`;
    }
    if (this.isTokenizationProject) {
      return '';
    }
    if (this.isEmailField(field) && !EMAIL_PATTERN.test(next)) {
      return 'Enter a valid email address.';
    }
    if (this.isPhoneField(field)) {
      const digits = this.normalizePhoneValue(next);
      if (!/^\d+$/.test(digits) || digits.length < PHONE_MIN_DIGITS || digits.length > PHONE_MAX_DIGITS) {
        return 'Enter a valid phone number.';
      }
    }
    if (this.isWhatsappField(field)) {
      const digits = this.normalizePhoneValue(next);
      if (!/^\d+$/.test(digits) || digits.length < PHONE_MIN_DIGITS || digits.length > PHONE_MAX_DIGITS) {
        return 'Enter a valid WhatsApp number.';
      }
    }
    return '';
  }

  private normalizeImportValue(key: string, value: string): string {
    let next = String(value ?? '').trim();
    if (!next) {
      return '';
    }
    if (this.isTokenizationProject) {
      return next;
    }
    const field = String(key || '');
    if (this.isPhoneField(field) || this.isWhatsappField(field)) {
      return this.normalizePhoneValue(next);
    }
    if (this.isEmailField(field) || EMAIL_PATTERN.test(next)) {
      return next.toLowerCase();
    }
    return next;
  }

  private csvDataToMatrix(data: unknown[]): string[][] {
    const first = data[0];
    if (first && !Array.isArray(first) && typeof first === 'object') {
      const keys = Object.keys(first as object);
      return [
        keys,
        ...data.map((row) => keys.map((key) => String((row as Record<string, unknown>)?.[key] ?? ''))),
      ];
    }
    return data.map((row) => (
      Array.isArray(row) ? row.map((cell) => String(cell ?? '')) : [String(row ?? '')]
    ));
  }

  private normalizeCsvHeader(value: string): string {
    return String(value || '').replace(/^\ufeff/, '').trim().toLowerCase().replace(/\s+/g, '_');
  }

  private resolveCsvField(header: string, allowed: Set<string>): string {
    const normalized = this.normalizeCsvHeader(header);
    const raw = String(header || '').replace(/^\ufeff/, '').trim();
    if (allowed.has(normalized)) {
      return normalized;
    }
    if (allowed.has(raw)) {
      return raw;
    }
    const option = this.fieldOptions.find((item) => (
      item.id === raw
      || item.id === normalized
      || this.normalizeCsvHeader(item.label) === normalized
    ));
    if (option && allowed.has(option.id)) {
      return option.id;
    }
    return '';
  }

  private parseCsvMatrix(
    matrix: string[][],
    defaultKey: string,
    allowedFields: string[],
  ): { rows: Array<{ key: string; value: string }>; columns: string[]; grid: string[][]; invalid: string[] } {
    const allowed = new Set(allowedFields);
    const empty = { rows: [] as Array<{ key: string; value: string }>, columns: [] as string[], grid: [] as string[][], invalid: ['The file is empty.'] };
    if (!matrix.length) {
      return empty;
    }
    const header = (matrix[0] || []).map((cell) => this.normalizeCsvHeader(cell));
    const columns = (matrix[0] || []).map((cell) => this.resolveCsvField(cell, allowed));
    const isValueOnly = header.length === 1 && header[0] === 'value';
    const isLegacy = header.includes('field') || header.includes('key') || isValueOnly;
    const isWide = !isLegacy && columns.some((key) => Boolean(key));
    const rows: Array<{ key: string; value: string }> = [];
    const invalid: string[] = [];
    if (isWide) {
      columns.forEach((key, column) => {
        const title = String(matrix[0]?.[column] || '').replace(/^\ufeff/, '').trim();
        if (title && !key) {
          invalid.push(`Column ${column + 1}: ${title} is not a selected blacklist field.`);
        }
      });
      const visibleColumns = columns.filter(Boolean);
      const grid: string[][] = [];
      for (let index = 1; index < matrix.length; index += 1) {
        const line = matrix[index] || [];
        const gridRow: string[] = [];
        let hasValue = false;
        columns.forEach((key, column) => {
          if (!key) {
            return;
          }
          const value = String(line[column] || '').trim();
          gridRow.push(value);
          if (!value) {
            return;
          }
          const invalidValue = this.blacklistValueError(key, value);
          if (invalidValue) {
            invalid.push(`Row ${index + 1}: ${invalidValue}`);
            return;
          }
          hasValue = true;
          rows.push({ key, value });
        });
        if (hasValue) {
          grid.push(gridRow);
        }
      }
      return { rows, columns: visibleColumns, grid, invalid };
    }
    const hasHeader = isLegacy;
    const fieldIndex = hasHeader && !isValueOnly
      ? (header.indexOf('field') >= 0 ? header.indexOf('field') : header.indexOf('key'))
      : -1;
    const valueIndex = hasHeader && header.includes('value') ? header.indexOf('value') : (hasHeader ? 1 : 0);
    const start = hasHeader ? 1 : 0;
    for (let index = start; index < matrix.length; index += 1) {
      const line = matrix[index] || [];
      const key = String(fieldIndex >= 0 ? line[fieldIndex] : defaultKey || '').trim();
      const value = String(line[valueIndex >= 0 ? valueIndex : 0] || '').trim();
      if (!key && !value) {
        continue;
      }
      if (!key) {
        invalid.push(`Row ${index + 1}: missing field.`);
        continue;
      }
      if (allowed.size && !allowed.has(key)) {
        invalid.push(`Row ${index + 1}: ${key} is not a selected blacklist field.`);
        continue;
      }
      if (!value) {
        continue;
      }
      const invalidValue = this.blacklistValueError(key, value);
      if (invalidValue) {
        invalid.push(`Row ${index + 1}: ${invalidValue}`);
        continue;
      }
      rows.push({ key, value });
    }
    return { rows, columns: [], grid: [], invalid };
  }
}
